import { type FunctionComponent } from "react";
import { StarFilled, TrophyOutlined } from "@ant-design/icons";
import { Popover, Progress, Space, Tag, Typography } from "antd";

import { type ClientRatingBreakdown } from "@fitness-app/data-models/entities/ClientRating";

import { useTraineeRating } from "~/modules/Trainee/TraineeProfile/scope";
import { clientRatingBreakdownLabels } from "../../helpers/clientRatingLabels";

const GOLD = "#faad14";

type Props = {
  traineeId: string;
};

const TraineeRatingBadge: FunctionComponent<Props> = ({ traineeId }) => {
  const { data: rating } = useTraineeRating();

  if (!rating || rating.traineeId !== traineeId) {
    return null;
  }

  const isTopLevel = rating.nextLevel === null || rating.pointsToNextLevel === null;
  const progressToNextLevel =
    !isTopLevel && rating.pointsToNextLevel !== null
      ? Math.min(100, Math.round((rating.ratingPoints / (rating.ratingPoints + rating.pointsToNextLevel)) * 100))
      : 100;

  const topActivities = Object.entries(rating.breakdown ?? {})
    .filter(([, points]) => points > 0)
    .sort(([, a], [, b]) => b - a);

  const popoverContent = (
    <Space direction="vertical" size={10} style={{ maxWidth: 280 }}>
      <Space size={6} align="center">
        <Tag color="gold" icon={isTopLevel ? <TrophyOutlined /> : undefined} style={{ marginRight: 0 }}>
          Level {rating.level}
        </Tag>
        <Typography.Text type="secondary">
          Całość: <Typography.Text strong>{rating.lifetimePoints} pkt</Typography.Text> · ost. 30 dni: +
          {rating.recentPoints} pkt
        </Typography.Text>
      </Space>

      <div>
        <Progress percent={progressToNextLevel} size="small" showInfo={false} strokeColor={GOLD} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {isTopLevel ? "Masz najwyższy dostępny level" : `${rating.pointsToNextLevel} pkt do L${rating.nextLevel}`}
        </Typography.Text>
      </div>

      {topActivities.length > 0 && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Skąd gwiazdki
          </Typography.Text>
          <div style={{ marginTop: 6 }}>
            <Space wrap size={[4, 6]}>
              {topActivities.map(([key, points]) => (
                <Tag color="gold" key={key} style={{ marginRight: 0 }}>
                  {clientRatingBreakdownLabels[key as keyof ClientRatingBreakdown]}: {points}
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      )}
    </Space>
  );

  return (
    <Popover
      title={
        <Space size={6}>
          <StarFilled style={{ color: GOLD }} />
          Hollywood Stars
        </Space>
      }
      content={popoverContent}
      trigger="hover"
      placement="bottomLeft"
    >
      <Tag color="gold" style={{ cursor: "default", marginRight: 0 }}>
        <StarFilled style={{ color: GOLD, marginRight: 4 }} />
        {rating.ratingPoints} pkt · P{rating.level}
      </Tag>
    </Popover>
  );
};

export default TraineeRatingBadge;
